import { useRef } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay/legacy';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes';
import { hasPersonalizedFilterQueryParam } from '../../shared/helpers/personalizedFiltersHelpers';
import { handleSbUserCountryCode } from '../helpers/handleSbUserCountryCode';

/* Components */
import { handlePageRefetchVariableOnUserChange } from '../../shared/handlePageRefetchVariableOnUserChange';
import { SbSharedRefetchContainer } from '../SbSharedRefetchContainer/SbSharedRefetchContainer';
import { SbRespDesignsComponents } from '../SbRespDesignsComponents/SbRespDesignsComponents';
import { SbSharedHead } from '../SbSharedHead/SbSharedHead';
import {
    RecentlyViewedItemsProvider,
    TABLET,
    DESKTOP,
} from 'dibs-recent-history/exports/RecentlyViewedItemsProvider';
import { SbSharedFavoritesNudgeToast } from '../../shared/SharedFavoritesNudge/SbSharedFavoritesNudgeToast/SbSharedFavoritesNudgeToast';

/* Hooks */
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { useSearchCorrections } from '../useSearchCorrections';
import { useSharedUserAndRelayVarsLoader } from '../../shared/SharedUserAndRelayVarLoader';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage';
import { useSbSharedPageTracking } from '../hooks/useSbSharedPageTracking';
import { sbRespRefineMenuFiltersMap } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap';

const SbRespDesignsLayoutComponent = ({
    viewer,
    reinitTracking,
    relay,
    runNotificationQueue = () => {},
}) => {
    const relayVars = useRef({});
    const { isTablet, isItemFavoritingVariant } = useServerVarsContext();
    const { itemSearch, regionalInfo = null, shippingFilters = null, user = null } = viewer;
    const userId = user?.serviceId;

    useDwelledOnPage(PAGE_TYPE.SEARCH);

    function doRefetch(variables, renderVariables, callback) {
        relayVars.current = {
            ...relayVars.current,
            ...variables,
            userCountryCode: handleSbUserCountryCode({
                userCountryCode: variables?.userCountryCode,
                fetchSb: variables?.fetchSb,
            }),
        };
        relay.refetch(
            relayVars.current,
            { ...relayVars.current, ...renderVariables, fetchSb: true },
            callback
        );
    }

    function onUserRefetch(variables, ...args) {
        const { isClient, fetchSbOnUserChange } = variables || {};
        const hasPersonalizedFilter = isClient && hasPersonalizedFilterQueryParam();
        //Personal-rerank needs to refetch s&b after login
        const fetchSb = handlePageRefetchVariableOnUserChange(variables) || fetchSbOnUserChange;

        doRefetch(
            {
                ...variables,
                fetchSb,
                skipUser: false,
                hasPersonalizedFilter,
            },
            ...args
        );
    }

    function handleSbRefetch(variables, renderVariables, callback) {
        doRefetch({ ...variables, fetchSb: true }, renderVariables, () => {
            callback();

            runNotificationQueue();
        });
    }

    useSharedUserAndRelayVarsLoader({ onUserRefetch });
    const [searchCorrectionTerm] = useSearchCorrections({ itemSearch });
    useSbSharedPageTracking({
        viewer,
        itemSearch,
        user,
        reinitTracking,
        refineMenuFiltersMap: sbRespRefineMenuFiltersMap,
        searchCorrectionTerm,
    });

    return (
        <RecentlyViewedItemsProvider deviceType={isTablet ? TABLET : DESKTOP}>
            <SbSharedHead
                itemSearch={itemSearch}
                imageFallback={itemSearch?.browseContentModule?.mainImage}
            />
            <SbSharedRefetchContainer
                viewer={viewer}
                itemSearch={itemSearch}
                onSbRefetch={handleSbRefetch}
                regionalInfo={regionalInfo}
                shippingFilters={shippingFilters}
                shouldShowSpinner={false}
            />
            <SbRespDesignsComponents
                viewer={viewer}
                itemSearch={itemSearch}
                user={user}
                isClient={!!relayVars.current.isClient}
                shippingFilters={shippingFilters}
            />
            <SbSharedFavoritesNudgeToast
                userId={userId}
                isItemFavoritingVariant={isItemFavoritingVariant}
            />
        </RecentlyViewedItemsProvider>
    );
};

SbRespDesignsLayoutComponent.propTypes = {
    viewer: PropTypes.object,
    relay: PropTypes.object.isRequired,
    runNotificationQueue: PropTypes.func.isRequired,
    reinitTracking: PropTypes.func,
};

export const SbRespDesignsLayout = createRefetchContainer(
    SbRespDesignsLayoutComponent,
    {
        viewer: graphql`
            fragment SbRespDesignsLayout_viewer on Viewer
            @argumentDefinitions(
                hasPersonalizedFilter: { type: "Boolean", defaultValue: false }
                userZipCode: { type: "String", defaultValue: "" }
                fetchRegionalInfo: { type: "Boolean", defaultValue: false }
            ) {
                ...SbRespDesignsComponents_viewer
                ...useSbSharedPageTracking_viewer
                itemSearch(
                    userId: $personalizationId
                    guestId: $guestId
                    uriRef: $uriRef
                    originalUrl: $originalUrl
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    regions: $regions
                    disableForceFacet: $disableForceFacet
                    disableNonParameterizedUrlRedirects: $disableNonParameterizedUrlRedirects
                    userCountryCode: $userCountryCode
                    regionsList: $regionsList
                    includeSponsoredItems: $includeSponsoredItems
                    priceBookName: $priceBookName
                    numOfItemsToInflate: $numOfItemsToInflate
                ) @include(if: $fetchSb) {
                    ...useSearchCorrections_itemSearch
                    ...SbSharedRefetchContainer_itemSearch
                    ...SbRespDesignsComponents_itemSearch
                    ...SbSharedHead_itemSearch
                    ...useSbSharedPageTracking_itemSearch
                    browseContentModule(contentModuleId: $contentModuleId) {
                        ... on ContentModuleIconicDesign {
                            mainImage
                        }
                    }
                }
                user(userId: $userId) @include(if: $fetchUser) {
                    serviceId
                    ...useSbSharedPageTracking_user
                    ...SbRespDesignsComponents_user
                }
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    ...SbSharedRefetchContainer_regionalInfo
                }
                shippingFilters(
                    uriRef: $uriRef
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                ) @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_shippingFilters
                    ...SbRespDesignsComponents_shippingFilters
                }
            }
        `,
    },
    graphql`
        query SbRespDesignsLayoutRefetchQuery(
            $userId: String = ""
            $personalizationId: String = ""
            $guestId: String = ""
            $rerankUserId: String = ""
            $rerankGuestId: String = ""
            $fetchUser: Boolean!
            $fetchSb: Boolean!
            $skipUser: Boolean = true
            $isTrade: Boolean!
            $uriRef: String
            $originalUrl: String
            $first: Int!
            $page: Int!
            $localeOrigin: String = ""
            $showSeller: Boolean!
            $followSearchPages: [String]
            $followSearchTypes: [types] = [SEARCH, USER_PREFERENCE]
            $contentModuleId: String = ""
            $regions: String = ""
            $hasPersonalizedFilter: Boolean!
            $userZipCode: String = ""
            $userCountryCode: String = ""
            $fetchRegionalInfo: Boolean = false
            $disableForceFacet: Boolean = true
            $isClient: Boolean!
            $disableNonParameterizedUrlRedirects: Boolean = false # Only used for static SB
            $fetchTileVideo: Boolean = false
            $priceBookName: String
            $regionsList: [String]
            $includeSponsoredItems: Boolean = false
            $pageDisplayEnum: PageDisplayEnum = searchAndBrowse
            $numOfItemsToInflate: Int
        ) {
            viewer {
                ...SbRespDesignsComponents_viewer
                ...useSbSharedPageTracking_viewer
                itemSearch(
                    userId: $personalizationId
                    guestId: $guestId
                    uriRef: $uriRef
                    originalUrl: $originalUrl
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    regions: $regions
                    disableForceFacet: $disableForceFacet
                    disableNonParameterizedUrlRedirects: $disableNonParameterizedUrlRedirects
                    userCountryCode: $userCountryCode
                    regionsList: $regionsList
                    includeSponsoredItems: $includeSponsoredItems
                    priceBookName: $priceBookName
                    numOfItemsToInflate: $numOfItemsToInflate
                ) @include(if: $fetchSb) {
                    ...SbSharedRefetchContainer_itemSearch
                    ...SbRespDesignsComponents_itemSearch
                    ...SbSharedHead_itemSearch
                    ...useSbSharedPageTracking_itemSearch
                }
                user(userId: $userId) @include(if: $fetchUser) @skip(if: $skipUser) {
                    ...useSbSharedPageTracking_user
                    ...SbRespDesignsComponents_user
                }
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    ...SbSharedRefetchContainer_regionalInfo
                }
                shippingFilters(
                    uriRef: $uriRef
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                ) @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_shippingFilters
                    ...SbRespDesignsComponents_shippingFilters
                }
            }
        }
    `
);
